import { useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Seo } from "@/seo/Seo";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, Check, FileText, ShieldCheck, CreditCard, PenLine, ChevronDown, Clock,
  CalendarClock, MessageSquare, ExternalLink, CheckCircle2, FileDown,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  workspaceAccess, proposalAccept, proposalRequestChanges, agreementAccept, paymentCheckout,
  workspaceDepositSummary, workspaceDepositCheckout,
  deliverableApprove, deliverableRevise, workspaceMessage, projectPaymentCheckout,
  workspaceDownloadDocument,
} from "@/lib/api";

const money = (v, c = "usd") =>
  v == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: (c || "usd").toUpperCase(), maximumFractionDigits: 2 }).format(v);
const solLabel = { minimal: "Minimal", recommended: "Recommended", gtm_ready: "GTM Ready" };

function Header({ navigate }) {
  return (
    <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
      <Seo noindex path="/workspace" title="Client Workspace · AIROTECH" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
        <button onClick={() => navigate("/")}><img src="/airotech-mark.png" alt="AIROTECH" className="h-8 w-auto" /></button>
        <div className="mono-label text-zinc-500">CLIENT WORKSPACE</div>
      </div>
    </header>
  );
}

function Section({ title, icon: Icon, children, id, testid }) {
  return (
    <section id={id} data-testid={testid} className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-7 md:p-10">
      <div className="mono-label text-cyan-400 mb-6 flex items-center gap-2">{Icon && <Icon className="h-4 w-4" />} {title}</div>
      {children}
    </section>
  );
}

/* ---------------- Gate ---------------- */
function Gate({ email, setEmail, open, busy }) {
  return (
    <div className="max-w-xl mx-auto px-6 md:px-10 pt-24 pb-24">
      <div className="mono-label text-cyan-400 mb-6">AIROTECH CLIENT WORKSPACE</div>
      <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">Enter your workspace.</h1>
      <p className="mt-6 text-zinc-400 text-lg leading-relaxed">Confirm the email you used on your intake to access your engagement securely.</p>
      <div className="mt-10 space-y-3">
        <Label className="mono-label text-zinc-400">Your email</Label>
        <Input data-testid="workspace-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && open()} className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-14 text-base" placeholder="you@company.com" />
      </div>
      <button data-testid="workspace-open" onClick={open} disabled={busy}
        className="mt-8 inline-flex items-center gap-2 bg-white text-black font-medium px-8 py-4 rounded-full mono-label hover:bg-cyan-400 transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enter workspace <ArrowRight className="h-4 w-4" /></>}
      </button>
    </div>
  );
}

/* ---------------- Status rail ---------------- */
function StatusRail({ d, onPrimary, busy }) {
  const pa = d.primary_action || { key: "none" };
  const ps = d.payment_summary;
  const disabled = pa.key === "none" || pa.key === "payment_processing";
  return (
    <div className="lg:sticky lg:top-[100px] space-y-6">
      <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-b from-cyan-400/[0.06] to-transparent p-7">
        <div className="mono-label text-zinc-500">CURRENT STATUS</div>
        <div data-testid="workspace-status" className="mt-2 font-heading text-2xl text-white leading-tight">{d.status_label}</div>
        {pa.label && (
          <button data-testid="workspace-primary-action" onClick={onPrimary} disabled={disabled || busy}
            className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full mono-label font-medium transition-colors ${disabled ? "bg-[#141414] text-zinc-500 cursor-default" : "bg-cyan-400 text-black hover:bg-cyan-300"} disabled:opacity-70`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : pa.key === "payment_processing" ? <Clock className="h-4 w-4" /> : null}
            {pa.label}{!disabled && <ArrowRight className="h-4 w-4" />}
          </button>
        )}
      </div>

      <div className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-7">
        <div className="mono-label text-zinc-500 mb-4">YOUR JOURNEY</div>
        <div className="space-y-3" data-testid="workspace-journey">
          {d.journey.map((s) => (
            <div key={s.key} className="flex items-center gap-3 text-sm">
              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] shrink-0 ${s.state === "done" ? "bg-cyan-400 text-black" : s.state === "current" ? "border-2 border-cyan-400 text-cyan-300" : "border border-zinc-700 text-zinc-600"}`}>
                {s.state === "done" ? "✓" : s.state === "current" ? "●" : ""}
              </span>
              <span className={s.state === "todo" ? "text-zinc-600" : s.state === "current" ? "text-cyan-300 font-medium" : "text-zinc-300"}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {ps && (
        <div className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-7" data-testid="workspace-payment-summary">
          <div className="mono-label text-zinc-500 mb-4">PAYMENT</div>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Total investment</span><span className="text-white">{money(ps.total, ps.currency)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Paid</span><span className="text-emerald-300">{money(ps.paid, ps.currency)}</span></div>
            <div className="flex justify-between border-t border-[#161616] pt-2.5"><span className="text-zinc-500">Remaining</span><span className="text-white">{money(ps.remaining, ps.currency)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Proposal document ---------------- */
function ProposalDoc({ p, proposalRef }) {
  const s = p.scope || {};
  const list = (arr) => Array.isArray(arr) && arr.length > 0;
  return (
    <section ref={proposalRef} data-testid="workspace-proposal" className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-7 md:p-12">
      <div className="mono-label text-cyan-400">YOUR AIROTECH PROPOSAL</div>
      <h2 className="mt-4 font-heading font-extrabold tracking-tighter text-3xl md:text-4xl text-white">{p.solution?.solution_name || solLabel[p.selected_solution]}</h2>

      <div className="mt-8 grid sm:grid-cols-3 gap-6">
        <div><div className="mono-label text-zinc-500">SOLUTION</div><div className="mt-1 text-white text-lg">{solLabel[p.selected_solution]}</div></div>
        <div><div className="mono-label text-zinc-500">BUILD LEVEL</div><div className="mt-1 text-white text-lg">{p.build_level_label}</div></div>
        <div><div className="mono-label text-zinc-500">INVESTMENT</div><div className="mt-1 text-cyan-300 text-lg font-medium" data-testid="workspace-price">{money(p.final_price, p.currency)}</div></div>
      </div>

      {list(p.payment_items) && (
        <div className="mt-8">
          <div className="mono-label text-zinc-500 mb-3">PAYMENT STRUCTURE</div>
          <div className="space-y-2">
            {p.payment_items.map((it, i) => (
              <div key={i} className="flex items-center justify-between border border-[#161616] rounded-xl px-4 py-3">
                <span className="text-zinc-300 text-sm">{it.name}{it.due_condition ? ` · ${it.due_condition}` : ""}</span>
                <span className="text-white text-sm">{it.percentage != null ? `${it.percentage}%` : ""} · {money(it.amount, p.currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {s.executive_summary && <Block title="EXECUTIVE SUMMARY">{s.executive_summary}</Block>}
      {s.business_objective && <Block title="BUSINESS OBJECTIVE">{s.business_objective}</Block>}
      {s.scope_summary && <Block title="SCOPE">{s.scope_summary}</Block>}

      <div className="mt-8 grid md:grid-cols-2 gap-8">
        {list(s.included_capabilities) && (
          <div><div className="mono-label text-emerald-300 mb-3">INCLUDED</div>
            <ul className="space-y-2">{s.included_capabilities.map((c, i) => <li key={i} className="flex gap-2.5 text-zinc-300 text-sm"><Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />{c}</li>)}</ul></div>
        )}
        {list(s.excluded_capabilities) && (
          <div><div className="mono-label text-zinc-500 mb-3">NOT INCLUDED</div>
            <ul className="space-y-2">{s.excluded_capabilities.map((c, i) => <li key={i} className="text-zinc-500 text-sm">— {c}</li>)}</ul></div>
        )}
      </div>

      {list(s.implementation_phases) && (
        <div className="mt-8"><div className="mono-label text-zinc-500 mb-3">IMPLEMENTATION</div>
          <div className="space-y-3">{s.implementation_phases.map((ph, i) => (
            <div key={i} className="flex gap-4"><span className="mono-label text-cyan-400/70 shrink-0 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <div><div className="text-white text-sm">{ph.name}</div>{ph.focus && <div className="text-zinc-500 text-sm">{ph.focus}</div>}</div></div>
          ))}</div>
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-2 gap-8">
        {list(s.client_responsibilities) && <ListBlock title="YOUR RESPONSIBILITIES" items={s.client_responsibilities} />}
        {list(s.airotech_responsibilities) && <ListBlock title="AIROTECH RESPONSIBILITIES" items={s.airotech_responsibilities} />}
      </div>

      {s.next_steps && <Block title="NEXT STEPS">{s.next_steps}</Block>}
      {p.valid_until && <p className="mt-8 mono-label text-zinc-600">PROPOSAL VALID UNTIL {new Date(p.valid_until).toLocaleDateString()}</p>}
    </section>
  );
}
const Block = ({ title, children }) => (
  <div className="mt-8"><div className="mono-label text-zinc-500 mb-2">{title}</div><p className="text-zinc-300 leading-relaxed">{children}</p></div>
);
const ListBlock = ({ title, items }) => (
  <div><div className="mono-label text-zinc-500 mb-3">{title}</div><ul className="space-y-2">{items.map((c, i) => <li key={i} className="text-zinc-400 text-sm">• {c}</li>)}</ul></div>
);

/* ---------------- Product Review reference ---------------- */
function ReviewReference({ rev, recKey }) {
  const [open, setOpen] = useState(false);
  const keys = [["minimal", "Minimal"], ["recommended", "Recommended"], ["gtm_ready", "GTM Ready"]];
  return (
    <section className="rounded-3xl border border-[#161616] bg-[#080808] overflow-hidden" data-testid="workspace-product-review">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between p-7 md:px-10">
        <div className="mono-label text-cyan-400 flex items-center gap-2"><FileText className="h-4 w-4" /> YOUR PRODUCT REVIEW</div>
        <ChevronDown className={`h-5 w-5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-7 md:px-10 pb-10 space-y-6">
          {rev.what_we_heard && <Block title="WHAT WE HEARD">{rev.what_we_heard}</Block>}
          {rev.opportunity && <Block title="OPPORTUNITY">{rev.opportunity}</Block>}
          {rev.product_definition && <Block title="PRODUCT DEFINITION">{rev.product_definition}</Block>}
          <div className="grid md:grid-cols-3 gap-4 pt-2">
            {keys.map(([k, l]) => rev[k] && (
              <div key={k} className={`rounded-2xl border p-5 ${recKey === k ? "border-cyan-400/40 bg-cyan-400/[0.04]" : "border-[#161616]"}`}>
                <div className="mono-label text-zinc-500">{l}{recKey === k ? " · RECOMMENDED" : ""}</div>
                <div className="mt-1 text-white text-sm font-medium">{rev[k].solution_name}</div>
                <div className="mt-1 mono-label text-cyan-400/80">{rev[k].build_level_label}</div>
                <p className="mt-2 text-zinc-500 text-xs leading-relaxed">{rev[k].summary}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------- Customer Document Portal ---------------- */
function DocumentsSection({ documents, token, email }) {
  const [busy, setBusy] = useState("");
  const download = async (id, fmt) => {
    setBusy(id + fmt);
    try { await workspaceDownloadDocument(token, email, id, fmt); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not open document."); }
    finally { setBusy(""); }
  };
  return (
    <Section title="YOUR DOCUMENTS" icon={FileText} testid="workspace-documents">
      <p className="text-zinc-400 text-sm mb-6 leading-relaxed">Documents AIROTECH Inc. has prepared for you. Open or download the PDF or Word version anytime.</p>
      <div className="space-y-3">
        {documents.map((doc) => (
          <div key={doc.document_id} data-testid={`workspace-document-${doc.document_id}`}
            className="flex items-center justify-between gap-4 rounded-2xl border border-[#161616] bg-[#080808] px-5 py-4 flex-wrap hover:border-cyan-400/30 transition-colors">
            <div className="min-w-0">
              <div className="text-white text-sm font-medium truncate">{doc.title}</div>
              <div className="mono-label text-zinc-500 text-[10px] mt-1">v{doc.version} · {(doc.created_at || "").slice(0, 10)} · {doc.prepared_by}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`workspace-doc-pdf-${doc.document_id}`} onClick={() => download(doc.document_id, "pdf")} disabled={!!busy}
                className="mono-label text-[11px] inline-flex items-center gap-1.5 bg-cyan-400 text-black rounded-full px-4 py-2 hover:bg-cyan-300 transition-colors disabled:opacity-50">
                {busy === doc.document_id + "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
              </button>
              <button data-testid={`workspace-doc-docx-${doc.document_id}`} onClick={() => download(doc.document_id, "docx")} disabled={!!busy}
                className="mono-label text-[11px] inline-flex items-center gap-1.5 border border-[#232323] text-zinc-300 rounded-full px-4 py-2 hover:border-cyan-400/40 transition-colors disabled:opacity-50">
                {busy === doc.document_id + "docx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Word
              </button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Project workspace (Phase 6) ---------------- */
function DepositPanel({ token, email }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const dmoney = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const load = async () => { try { setS(await workspaceDepositSummary(token, email)); } catch { /* ignore */ } };
  useEffect(() => {
    if (!token || !email) return;
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, email]);
  if (!s || !s.available) return null;
  const pay = async () => {
    setBusy(true);
    try { const r = await workspaceDepositCheckout(token, email, window.location.origin); window.location.href = r.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment."); setBusy(false); }
  };
  const Row = ({ label, value, strong, accent }) => (
    <div className="flex items-center justify-between py-2 border-b border-[#141414] last:border-0">
      <span className={`text-sm ${strong ? "text-white" : "text-zinc-400"}`}>{label}</span>
      <span className={`text-sm ${accent ? "text-cyan-300" : strong ? "text-white font-medium" : "text-zinc-300"}`}>{value}</span>
    </div>
  );
  if (s.deposit_paid) {
    return (
      <section data-testid="deposit-paid-panel" className="rounded-3xl border border-emerald-400/25 bg-emerald-400/[0.05] p-7 md:p-9">
        <div className="flex items-center gap-2 mono-label text-emerald-300"><CheckCircle2 className="h-4 w-4" /> PAYMENT RECEIVED</div>
        <h2 className="font-heading font-extrabold tracking-tight text-2xl md:text-3xl mt-3">Your project is officially underway.</h2>
        <div className="mt-5 grid sm:grid-cols-2 gap-6">
          <div>
            <Row label="Selected build" value={s.selected_build || "—"} strong />
            <Row label="Total project" value={dmoney(s.total_project_price)} strong />
            <Row label="Deposit paid" value={dmoney(s.deposit_amount)} accent />
            <Row label="Remaining balance" value={dmoney(s.remaining_balance)} />
            <Row label="Current status" value={(s.project_status || "ACTIVE").replace(/_/g, " ")} strong />
          </div>
          <div>
            <div className="mono-label text-zinc-500 mb-2">PAYMENT SCHEDULE</div>
            {(s.milestone_payments || s.milestones).map((m, i) => (
              <div key={i} data-testid={`deposit-milestone-${m.milestone_number}`} className="flex items-center justify-between py-2 border-b border-[#141414] last:border-0">
                <span className="text-sm text-zinc-300">{m.label} — {m.percentage}%</span>
                <span className="text-sm text-white">{dmoney(m.amount)} {m.status ? <span className={`ml-2 mono-label text-[10px] ${m.status === "paid" ? "text-emerald-300" : m.status === "requested" ? "text-cyan-300" : "text-zinc-500"}`}>{m.status.toUpperCase()}</span> : null}</span>
              </div>
            ))}
            {s.next_milestone && <div className="mt-3 text-xs text-zinc-500">Next milestone: <span className="text-white">{s.next_milestone.label}</span></div>}
          </div>
        </div>
      </section>
    );
  }
  return (
    <section data-testid="deposit-select-panel" className="rounded-3xl border border-cyan-400/25 bg-cyan-400/[0.05] p-7 md:p-9">
      <div className="mono-label text-cyan-400">YOU'VE SELECTED YOUR BUILD</div>
      <h2 className="font-heading font-extrabold tracking-tight text-2xl md:text-3xl mt-3">{s.selected_build || "Your build"}</h2>
      <div className="mt-5 max-w-md">
        <Row label="Project investment" value={dmoney(s.total_project_price)} strong />
        <Row label="Required deposit (due now)" value={dmoney(s.deposit_amount)} accent strong />
        <Row label="Remaining balance" value={dmoney(s.remaining_balance)} />
        {s.milestones.map((m) => (
          <Row key={m.milestone_number} label={`${m.label} — ${m.percentage}%`} value={dmoney(m.amount)} />
        ))}
      </div>
      <button data-testid="pay-deposit-btn" onClick={pay} disabled={busy || !s.agreement_accepted}
        className="mt-6 inline-flex items-center gap-2 bg-cyan-400 text-black rounded-full px-6 py-3 mono-label font-semibold hover:bg-cyan-300 transition-colors disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} PAY {dmoney(s.deposit_amount)} DEPOSIT &amp; START
      </button>
      {!s.agreement_accepted && <p className="mt-3 text-xs text-amber-300">Please review and accept your agreement above before paying the deposit.</p>}
    </section>
  );
}


function ProjectWorkspace({ d, navigate, busy, onPrimary, onSchedule, onApprove, onRevise, onPay, onSendMessage, pmsg, setPmsg, token, email }) {
  const pj = d.project;
  const pa = pj.primary_action || { key: "none" };
  const pay = pj.payment;
  const disabled = pa.key === "none";
  return (
    <div data-testid="workspace-root" className="min-h-screen bg-[#050505] text-white">
      <Header navigate={navigate} />
      <div className="border-b border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="mono-label text-cyan-400 mb-4">WELCOME, {(d.first_name || "").toUpperCase()}</div>
          <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">{pj.project_name}</h1>
          <div className="mt-6 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" /><span data-testid="workspace-status" className="mono-label text-cyan-300">{pj.status_label}</span></span>
            {pj.scheduling_url && <button data-testid="workspace-schedule" onClick={() => onSchedule(pa.key === "schedule_kickoff" ? pj.kickoff_url : pj.scheduling_url)} className="inline-flex items-center gap-2 rounded-full border border-[#232323] px-4 py-2 mono-label text-white hover:border-cyan-400/50 transition-colors"><CalendarClock className="h-4 w-4" /> {pa.key === "schedule_kickoff" ? "Schedule kickoff" : "Schedule a meeting"}</button>}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-3 gap-8 items-start">
        {/* Status rail */}
        <div className="lg:col-span-1 lg:order-2 space-y-6">
          <div className="lg:sticky lg:top-[100px] space-y-6">
            <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-b from-cyan-400/[0.06] to-transparent p-7">
              <div className="mono-label text-zinc-500">CURRENT STATUS</div>
              <div className="mt-2 font-heading text-2xl text-white">{pj.status_label}</div>
              {pa.label && <button data-testid="workspace-primary-action" onClick={onPrimary} disabled={disabled || busy}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full mono-label font-medium transition-colors ${disabled ? "bg-[#141414] text-zinc-500" : "bg-cyan-400 text-black hover:bg-cyan-300"} disabled:opacity-70`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{pa.label}{!disabled && <ArrowRight className="h-4 w-4" />}</button>}
            </div>
            <div className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-7">
              <div className="mono-label text-zinc-500 mb-4">PROJECT JOURNEY</div>
              <div className="space-y-3" data-testid="workspace-journey">
                {pj.journey.map((s) => (
                  <div key={s.key} className="flex items-center gap-3 text-sm">
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] shrink-0 ${s.state === "done" ? "bg-cyan-400 text-black" : s.state === "current" ? "border-2 border-cyan-400 text-cyan-300" : "border border-zinc-700 text-zinc-600"}`}>{s.state === "done" ? "✓" : s.state === "current" ? "●" : ""}</span>
                    <span className={s.state === "todo" ? "text-zinc-600" : s.state === "current" ? "text-cyan-300 font-medium" : "text-zinc-300"}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {pay && (
              <div className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-7" data-testid="workspace-payment-summary">
                <div className="mono-label text-zinc-500 mb-4">PAYMENT</div>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-500">Investment</span><span className="text-white">{money(pay.total, pay.currency)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Paid</span><span className="text-emerald-300">{money(pay.paid, pay.currency)}</span></div>
                  <div className="flex justify-between border-t border-[#161616] pt-2.5"><span className="text-zinc-500">Remaining</span><span className="text-white">{money(pay.remaining, pay.currency)}</span></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="lg:col-span-2 lg:order-1 space-y-8">
          <DepositPanel token={token} email={email} />
          <Section title="YOUR PROJECT" icon={FileText}>
            <div className="grid sm:grid-cols-2 gap-6">
              <div><div className="mono-label text-zinc-500">BUILD LEVEL</div><div className="mt-1 text-white text-lg">{pj.build_level_label}</div></div>
              <div><div className="mono-label text-zinc-500">CURRENT MILESTONE</div><div className="mt-1 text-white text-lg">{pj.current_milestone?.name || "—"}</div></div>
            </div>
          </Section>

          {d.documents?.length > 0 && <DocumentsSection documents={d.documents} token={token} email={email} />}

          {/* Milestones + deliverables */}
          <Section title="MILESTONES" icon={CheckCircle2} id="proj-deliverables" testid="workspace-milestones">
            <div className="space-y-4">
              {pj.milestones.map((m) => (
                <div key={m.id} data-testid={`milestone-${m.id}`} className={`rounded-2xl border p-5 ${m.status === "in_progress" ? "border-cyan-400/40 bg-cyan-400/[0.04]" : "border-[#161616]"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4"><span className="mono-label text-cyan-400/70">{String(m.order + 1).padStart(2, "0")}</span>
                      <div><div className="text-white">{m.name}</div><div className="text-zinc-500 text-sm">{m.description}</div></div></div>
                    <span className={`mono-label rounded-full px-3 py-1 border shrink-0 ${m.status === "completed" || m.status === "approved" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" : m.status === "in_progress" ? "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" : "text-zinc-400 border-[#232323]"}`}>{m.status_label}</span>
                  </div>
                  {m.deliverables.map((dv) => (
                    <div key={dv.id} data-testid={`deliverable-${dv.id}`} className="mt-4 rounded-xl border border-[#1c1c1c] bg-[#080808] p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div><div className="text-white text-sm">{dv.name} <span className="text-zinc-600">v{dv.version}</span></div>{dv.description && <div className="text-zinc-500 text-xs mt-0.5">{dv.description}</div>}</div>
                        <span className={`mono-label rounded-full px-2.5 py-0.5 border text-xs ${dv.status === "approved" ? "text-emerald-300 border-emerald-400/30" : dv.status === "ready_for_review" ? "text-amber-300 border-amber-400/30" : "text-zinc-400 border-[#232323]"}`}>{dv.status_label}</span>
                      </div>
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        {dv.link && <a href={dv.link} target="_blank" rel="noreferrer" className="mono-label text-cyan-300 inline-flex items-center gap-1.5 text-xs"><ExternalLink className="h-3.5 w-3.5" /> View</a>}
                        {dv.status === "ready_for_review" && <>
                          <button data-testid={`approve-${dv.id}`} onClick={() => onApprove(dv.id)} disabled={busy} className="mono-label text-xs inline-flex items-center gap-1.5 bg-cyan-400 text-black rounded-full px-4 py-1.5 hover:bg-cyan-300 transition-colors disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Approve</button>
                          <button data-testid={`revise-${dv.id}`} onClick={() => onRevise(dv.id)} disabled={busy} className="mono-label text-xs inline-flex items-center gap-1.5 border border-[#232323] text-white rounded-full px-4 py-1.5 hover:border-zinc-500 transition-colors">Request revision</button>
                        </>}
                      </div>
                    </div>
                  ))}
                  {m.payment && <div className="mt-3 text-sm flex items-center justify-between border-t border-[#161616] pt-3">
                    <span className="text-zinc-400">{m.payment.name} · {money(m.payment.amount, pay?.currency)}</span>
                    {m.payment.state === "paid" ? <span className="mono-label text-emerald-300">PAID</span> : m.payment.state === "due" ? <button data-testid={`pay-${m.payment.id}`} onClick={() => onPay(m.payment.id)} disabled={busy} className="mono-label text-xs bg-cyan-400 text-black rounded-full px-4 py-1.5 hover:bg-cyan-300 transition-colors">Pay now</button> : <span className="mono-label text-zinc-500">{(m.payment.state || "").toUpperCase()}</span>}
                  </div>}
                </div>
              ))}
            </div>
          </Section>

          {/* Payment schedule */}
          {pay && pay.schedule.length > 0 && (
            <Section title="PAYMENTS" icon={CreditCard} testid="workspace-payment">
              <div className="space-y-2">
                {pay.schedule.map((it, i) => (
                  <div key={i} className="flex items-center justify-between border border-[#161616] rounded-xl px-4 py-3">
                    <span className="text-zinc-300 text-sm">{it.name}</span>
                    <div className="flex items-center gap-3"><span className="text-white text-sm">{money(it.amount, pay.currency)}</span>
                      {it.state === "paid" ? <span className="mono-label text-emerald-300 text-xs" data-testid="workspace-payment-received">✓ PAID</span> : it.state === "due" ? <button data-testid="workspace-make-payment" onClick={() => onPay(it.id)} disabled={busy} className="mono-label text-xs bg-cyan-400 text-black rounded-full px-4 py-1.5 hover:bg-cyan-300 transition-colors">Make payment</button> : <span className="mono-label text-zinc-500 text-xs">{(it.state || "upcoming").toUpperCase()}</span>}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Activity */}
          {pj.activity.length > 0 && (
            <Section title="PROJECT ACTIVITY">
              <div className="space-y-2.5">{pj.activity.map((e, i) => (
                <div key={i} className="flex gap-4 text-sm"><span className="mono-label text-zinc-600 shrink-0">{new Date(e.at).toLocaleDateString()}</span><span className="text-zinc-400">{e.type.replace(/_/g, " ").toLowerCase()}</span></div>
              ))}</div>
            </Section>
          )}

          {/* Messages */}
          <Section title="PROJECT MESSAGES" icon={MessageSquare} testid="workspace-messages">
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {pj.messages.length === 0 && <p className="text-zinc-500 text-sm">No messages yet. Ask us anything about your project.</p>}
              {pj.messages.map((m) => (
                <div key={m.id} className={`rounded-xl px-4 py-3 text-sm ${m.from === "client" ? "bg-cyan-400/[0.06] border border-cyan-400/20" : "bg-[#0f0f0f] border border-[#1c1c1c]"}`}>
                  <div className="mono-label text-zinc-500 mb-1 text-xs">{m.from === "client" ? "YOU" : "AIROTECH"}</div><div className="text-zinc-300">{m.body}</div></div>
              ))}
            </div>
            <div className="flex gap-3">
              <input data-testid="workspace-message-input" value={pmsg} onChange={(e) => setPmsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSendMessage()} className="flex-1 bg-[#0b0b0c] border border-[#1f1f1f] text-white rounded-lg px-3 py-2.5 text-sm" placeholder="Type a message…" />
              <button data-testid="workspace-message-send" onClick={onSendMessage} disabled={busy} className="mono-label bg-white text-black rounded-full px-5 py-2.5 hover:bg-cyan-400 transition-colors disabled:opacity-50">Send</button>
            </div>
          </Section>

          <Section title="WHAT HAPPENS NEXT">
            <p className="text-zinc-400 leading-relaxed">{pj.status === "completed" ? "Your project is complete. Your full history remains available here." : pj.status === "project_setup" ? "Schedule your kickoff meeting to begin. We'll then set up your milestones and keep you updated here." : "Track your milestones and deliverables above, approve work as it's delivered, and complete any payments when due. Schedule a meeting anytime."}</p>
          </Section>
        </motion.div>
      </div>
    </div>
  );
}


export default function Workspace() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState(null);
  const [showAgreement, setShowAgreement] = useState(false);
  const [sig, setSig] = useState("");
  const [changeMsg, setChangeMsg] = useState("");
  const [showChanges, setShowChanges] = useState(false);
  const [pmsg, setPmsg] = useState("");
  const proposalRef = useRef(null);
  const paymentRef = useRef(null);

  const load = async () => { const data = await workspaceAccess(token, email.trim()); setD(data); return data; };
  const open = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter the email you used on your intake.");
    setBusy(true);
    try { await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Not available yet."); }
    finally { setBusy(false); }
  };

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const doAccept = async () => {
    setBusy(true);
    try {
      await proposalAccept(token, email.trim());
      setD((x) => x ? { ...x, status_label: "Agreement Required", primary_action: { key: "accept_agreement", label: "Review & accept agreement" } } : x);
      toast.success("Proposal accepted."); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept."); } finally { setBusy(false); }
  };
  const doRequestChanges = async () => {
    setBusy(true);
    try { await proposalRequestChanges(token, email.trim(), changeMsg.trim()); toast.success("Change request sent."); setShowChanges(false); setChangeMsg(""); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); } finally { setBusy(false); }
  };
  const doSignAgreement = async () => {
    if (sig.trim().length < 2) return toast.error("Type your full name to sign.");
    setBusy(true);
    try {
      await agreementAccept(token, email.trim(), sig.trim());
      setD((x) => x ? { ...x, status_label: "Payment Required", primary_action: { key: "make_payment", label: "Make payment" } } : x);
      toast.success("Agreement accepted."); setShowAgreement(false); setSig(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept agreement."); } finally { setBusy(false); }
  };
  const doPay = async () => {
    setBusy(true);
    try { const res = await paymentCheckout(token, email.trim(), window.location.origin); window.location.href = res.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment."); setBusy(false); }
  };

  const onPrimary = () => {
    const k = d?.primary_action?.key;
    if (k === "review_proposal") scrollTo(proposalRef);
    else if (k === "accept_agreement") setShowAgreement(true);
    else if (k === "make_payment") doPay();
    else if (k === "complete_closing") { const u = d?.primary_action?.url || d?.closing_url; if (u) window.location.href = u; }
    else if (k === "view_payment" || k === "view_project") scrollTo(paymentRef);
  };

  // Phase 6 project handlers
  const openSched = (url) => { if (url) window.open(url, "_blank"); };
  const doApproveDeliverable = async (id) => {
    setBusy(true);
    try { await deliverableApprove(token, email.trim(), id); toast.success("Deliverable approved."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve."); } finally { setBusy(false); }
  };
  const doReviseDeliverable = async (id) => {
    const m = window.prompt("What revision would you like?");
    if (m == null) return;
    setBusy(true);
    try { await deliverableRevise(token, email.trim(), id, m); toast.success("Revision requested."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); } finally { setBusy(false); }
  };
  const doProjectPay = async (paymentId) => {
    setBusy(true);
    try { const r = await projectPaymentCheckout(token, email.trim(), paymentId, window.location.origin); window.location.href = r.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment."); setBusy(false); }
  };
  const doSendMessage = async () => {
    if (!pmsg.trim()) return;
    setBusy(true);
    try { await workspaceMessage(token, email.trim(), pmsg.trim()); setPmsg(""); toast.success("Message sent."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); } finally { setBusy(false); }
  };
  const onProjectPrimary = () => {
    const pj = d.project; const k = pj?.primary_action?.key;
    if (k === "schedule_kickoff") openSched(pj.kickoff_url);
    else if (k === "schedule_meeting") openSched(pj.scheduling_url);
    else if (k === "make_payment" && pj.next_payment) doProjectPay(pj.next_payment.id);
    else document.getElementById("proj-deliverables")?.scrollIntoView({ behavior: "smooth" });
  };

  if (d && d.project) return (
    <ProjectWorkspace d={d} navigate={navigate} busy={busy} pmsg={pmsg} setPmsg={setPmsg}
      token={token} email={email.trim()}
      onPrimary={onProjectPrimary} onSchedule={openSched} onApprove={doApproveDeliverable}
      onRevise={doReviseDeliverable} onPay={doProjectPay} onSendMessage={doSendMessage} />
  );

  if (!d) return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header navigate={navigate} />
      <Gate email={email} setEmail={setEmail} open={open} busy={busy} />
    </div>
  );

  const p = d.proposal;
  const ag = d.agreement;
  return (
    <div data-testid="workspace-root" className="min-h-screen bg-[#050505] text-white">
      <Header navigate={navigate} />

      {/* Hero */}
      <div className="border-b border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="mono-label text-cyan-400 mb-4">WELCOME, {(d.first_name || "").toUpperCase()}</div>
          <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">{d.product_name}</h1>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="mono-label text-cyan-300">{d.status_label}</span>
          </div>
        </div>
      </div>

      {/* Body grid */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-1 lg:order-2"><StatusRail d={d} onPrimary={onPrimary} busy={busy} /></div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="lg:col-span-2 lg:order-1 space-y-8">
          <DepositPanel token={token} email={email.trim()} />
          {p ? <ProposalDoc p={p} proposalRef={proposalRef} /> : (
            <Section title="COMMERCIAL PROPOSAL" icon={FileText}>
              <p className="text-zinc-400 leading-relaxed">Your commercial proposal is being prepared by AIROTECH. You'll receive an email the moment it's ready to review here.</p>
            </Section>
          )}

          {/* Proposal actions */}
          {p && ["sent", "viewed", "changes_requested"].includes(p.status) && (
            <div className="flex flex-wrap gap-3">
              <button data-testid="workspace-accept-proposal" onClick={doAccept} disabled={busy}
                className="inline-flex items-center gap-2 bg-cyan-400 text-black font-medium px-7 py-3.5 rounded-full mono-label hover:bg-cyan-300 transition-colors disabled:opacity-50">Accept proposal <ArrowRight className="h-4 w-4" /></button>
              <button data-testid="workspace-request-changes" onClick={() => setShowChanges(true)} disabled={busy}
                className="inline-flex items-center gap-2 border border-[#232323] text-white px-7 py-3.5 rounded-full mono-label hover:border-zinc-500 transition-colors">Request changes</button>
            </div>
          )}

          {d.product_review && <ReviewReference rev={d.product_review} recKey={d.recommended_solution_key} />}

          {d.documents?.length > 0 && <DocumentsSection documents={d.documents} token={token} email={email.trim()} />}

          {/* Agreement */}
          {ag && (
            <Section title={`SERVICE AGREEMENT · v${ag.version}`} icon={ShieldCheck} testid="workspace-agreement">
              {ag.status === "accepted" ? (
                <p className="text-emerald-300 mono-label">✓ ACCEPTED{ag.accepted_at ? ` · ${new Date(ag.accepted_at).toLocaleDateString()}` : ""}</p>
              ) : (
                <>
                  <p className="text-zinc-400 mono-label mb-5">STATUS — AWAITING ACCEPTANCE</p>
                  <button data-testid="workspace-view-agreement" onClick={() => setShowAgreement(true)} className="inline-flex items-center gap-2 border border-[#232323] text-white px-7 py-3.5 rounded-full mono-label hover:border-cyan-400/50 transition-colors"><PenLine className="h-4 w-4" /> Review &amp; accept agreement</button>
                </>
              )}
            </Section>
          )}

          {/* Payment */}
          {p && ["payment_pending", "payment_in_progress", "payment_complete", "ready_for_project"].includes(p.status) && (
            <div ref={paymentRef}>
              <Section title="PAYMENT" icon={CreditCard} testid="workspace-payment">
                {d.payment ? (
                  <>
                    <p className="text-emerald-300 mono-label text-base" data-testid="workspace-payment-received">✓ PAYMENT RECEIVED — {money(d.payment.amount, d.payment.currency)}</p>
                    <p className="text-zinc-500 text-sm mt-2">{d.payment.purpose}{d.payment.paid_at ? ` · ${new Date(d.payment.paid_at).toLocaleString()}` : ""}{d.payment.reference ? ` · Ref ${d.payment.reference}` : ""}</p>
                    {d.payment_summary && d.payment_summary.remaining > 0 && <p className="text-zinc-400 text-sm mt-4">Remaining balance of {money(d.payment_summary.remaining, d.payment_summary.currency)} will be scheduled as your project progresses.</p>}
                  </>
                ) : d.amount_due ? (
                  <>
                    <div className="grid sm:grid-cols-3 gap-6 mb-6">
                      <div><div className="mono-label text-zinc-500">TOTAL</div><div className="mt-1 text-white text-lg">{money(d.payment_summary?.total, p.currency)}</div></div>
                      <div><div className="mono-label text-zinc-500">CURRENT PAYMENT</div><div className="mt-1 text-cyan-300 text-lg font-medium">{money(d.amount_due.amount, p.currency)}</div></div>
                      <div><div className="mono-label text-zinc-500">REMAINING AFTER</div><div className="mt-1 text-white text-lg">{money((d.payment_summary?.remaining || 0) - d.amount_due.amount, p.currency)}</div></div>
                    </div>
                    <p className="mono-label text-amber-300">{d.amount_due.name.toUpperCase()} — PAYMENT REQUIRED</p>
                    <button data-testid="workspace-make-payment" onClick={doPay} disabled={busy}
                      className="mt-5 inline-flex items-center gap-2 bg-cyan-400 text-black font-medium px-7 py-3.5 rounded-full mono-label hover:bg-cyan-300 transition-colors disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Make payment <ArrowRight className="h-4 w-4" /></>}</button>
                  </>
                ) : <p className="text-zinc-400">No payment is currently due.</p>}
              </Section>
            </div>
          )}

          {/* What's next */}
          <Section title="WHAT HAPPENS NEXT">
            <p className="text-zinc-400 leading-relaxed">
              {p?.status === "ready_for_project"
                ? "Your commercial requirements are complete. Your engagement is moving into project setup — the AIROTECH team will be in touch with next steps shortly."
                : p?.status === "changes_requested"
                  ? "AIROTECH is reviewing your requested changes and will send you an updated proposal soon."
                  : "Complete each step above at your own pace. Once your agreement is accepted and any required payment is made, your engagement moves into project setup."}
            </p>
          </Section>
        </motion.div>
      </div>

      {/* Agreement modal */}
      {showAgreement && ag && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAgreement(false)}>
          <div className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[#1f1f1f] mono-label text-cyan-400">SERVICE AGREEMENT · v{ag.version}</div>
            <div className="p-6 overflow-y-auto text-sm text-zinc-300 agreement-body space-y-2" dangerouslySetInnerHTML={{ __html: ag.content }} />
            <div className="p-6 border-t border-[#1f1f1f] space-y-3">
              <Label className="mono-label text-zinc-400">Type your full name to sign</Label>
              <Input data-testid="workspace-agreement-sign" value={sig} onChange={(e) => setSig(e.target.value)} className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-11" placeholder="Your full legal name" />
              <div className="flex gap-3 pt-1">
                <button data-testid="workspace-agreement-accept" onClick={doSignAgreement} disabled={busy} className="inline-flex items-center gap-2 bg-cyan-400 text-black font-medium px-6 py-3 rounded-full mono-label hover:bg-cyan-300 transition-colors disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Accept &amp; sign <Check className="h-4 w-4" /></>}</button>
                <button onClick={() => setShowAgreement(false)} className="px-6 py-3 rounded-full mono-label border border-[#232323] text-zinc-400 hover:text-white">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request changes modal */}
      {showChanges && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowChanges(false)}>
          <div className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="mono-label text-cyan-400">REQUEST CHANGES</div>
            <textarea data-testid="workspace-change-message" value={changeMsg} onChange={(e) => setChangeMsg(e.target.value)} rows={5}
              className="w-full bg-[#0b0b0c] border border-[#1f1f1f] text-white rounded-lg px-3 py-2.5 text-sm" placeholder="What would you like to change?" />
            <div className="flex gap-3">
              <button data-testid="workspace-change-submit" onClick={doRequestChanges} disabled={busy} className="inline-flex items-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-full mono-label hover:bg-cyan-400 transition-colors disabled:opacity-50">Send request</button>
              <button onClick={() => setShowChanges(false)} className="px-6 py-3 rounded-full mono-label border border-[#232323] text-zinc-400 hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
